import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const TicketModal = ({
  ticket,
  isOpen,
  onClose,
  onSendMessage,
  onUpdateStatus,
}) => {
  const [messageText, setMessageText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Status options for Admin
  const STATUS_OPTIONS = ["Open", "In Progress", "Resolved", "Closed"];

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [ticket, isOpen, ticket?.messages]);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!messageText.trim() && selectedFiles.length === 0) return;
    setIsSending(true);
    try {
      await onSendMessage(ticket.id, messageText, selectedFiles);
      setMessageText("");
      setSelectedFiles([]);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!ticket) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full sm:max-w-xl bg-white rounded-t-[28px] sm:rounded-[32px] shadow-2xl overflow-hidden flex flex-col h-[95dvh] sm:h-auto sm:max-h-[80vh]"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            {/* HEADER - Updated with Status Update Feature */}
            <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-start">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#0B1C2D] text-white flex items-center justify-center font-black text-xl uppercase">
                  {ticket.requester?.name?.charAt(0) || "U"}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-black text-[#0B1C2D]">
                      {ticket.subject}
                    </h3>
                    {/* ✅ STATUS UPDATE DROPDOWN */}
                    <select
                      value={ticket.status}
                      onChange={(e) =>
                        onUpdateStatus(ticket.id, e.target.value)
                      }
                      className={`text-[9px] uppercase font-black px-2 py-1 rounded-md border cursor-pointer outline-none transition-all ${
                        ticket.status === "Open"
                          ? "bg-blue-50 text-blue-600 border-blue-100"
                          : ticket.status === "In Progress"
                            ? "bg-orange-50 text-orange-600 border-orange-100"
                            : "bg-green-50 text-green-600 border-green-100"
                      }`}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400">
                    <span>REQ: {ticket.requester?.name}</span>
                    <span>•</span>
                    <span className="uppercase text-slate-300">
                      {ticket.displayId}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-2 hover:bg-white rounded-full transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {/* CHAT AREA */}
            <div className="flex-1 overflow-y-auto p-6 bg-[#F8FAFC] space-y-6 custom-scrollbar">
              {ticket.messages?.map((msg, index) => {
                const isMe = msg.role === "admin";
                return (
                  <div
                    key={msg.id || index}
                    className={`flex gap-3 ${isMe ? "justify-end" : "justify-start"}`}
                  >
                    {!isMe && (
                      <div className="w-8 h-8 rounded-xl bg-slate-200 flex items-center justify-center overflow-hidden shrink-0 mt-1">
                        {ticket.requester?.avatar ? (
                          <img
                            src={ticket.requester.avatar}
                            alt="User"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="material-symbols-outlined text-[16px] text-slate-500">
                            person
                          </span>
                        )}
                      </div>
                    )}
                    <div
                      className={`flex flex-col ${isMe ? "items-end" : "items-start"} max-w-[80%]`}
                    >
                      <div
                        className={`px-4 py-3 text-sm shadow-sm ${
                          isMe
                            ? "bg-[#0B1C2D] text-white rounded-2xl rounded-tr-none"
                            : "bg-white text-slate-700 rounded-2xl rounded-tl-none border border-slate-100"
                        }`}
                      >
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {msg.message}
                        </p>
                        {msg.attachments?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-2">
                            {msg.attachments.map((file, i) => (
                              <a
                                key={i}
                                href={file.url}
                                target="_blank"
                                rel="noreferrer"
                                className="block p-1 bg-black/10 rounded-lg hover:bg-black/20"
                              >
                                {file.type?.includes("image") ? (
                                  <img
                                    src={file.url}
                                    alt="att"
                                    className="h-12 w-12 object-cover rounded"
                                  />
                                ) : (
                                  <span className="material-symbols-outlined text-white p-2">
                                    description
                                  </span>
                                )}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                        <span>{msg.senderName}</span>
                        <span>•</span>
                        <span>{formatTime(msg.timestamp)}</span>
                        {isMe && (
                          <span
                            className={`material-symbols-outlined text-[14px] ${msg.status === "seen" ? "text-blue-500" : "text-slate-300"}`}
                          >
                            done_all
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* --- INPUT AREA --- */}
            <div className="p-6 bg-white border-t border-slate-100">
              {/* ✅ ADDED CONDITIONAL CHECK: Hide input if status is "Closed" */}
              {ticket.status === "Closed" ? (
                <div className="bg-gray-50 rounded-2xl p-4 text-center border border-dashed border-gray-200 flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-gray-400 text-[20px]">
                    lock
                  </span>
                  <span className="text-sm font-bold text-gray-500 uppercase tracking-tighter">
                    This ticket is closed and no longer accepting replies
                  </span>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {/* Selected Files Preview */}
                  {selectedFiles.length > 0 && (
                    <div className="flex gap-2 pb-2">
                      {selectedFiles.map((file, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100"
                        >
                          <span className="text-[10px] font-black uppercase text-slate-500 truncate max-w-[100px]">
                            {file.name}
                          </span>
                          <button
                            onClick={() => removeFile(i)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <span className="material-symbols-outlined text-[14px]">
                              close
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actual Input Row */}
                  <div className="flex gap-3 items-center">
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="w-11 h-11 flex items-center justify-center bg-slate-50 rounded-xl text-slate-400 hover:text-[#0B1C2D] transition-colors"
                    >
                      <span className="material-symbols-outlined">
                        attach_file
                      </span>
                    </button>
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                    />

                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSend()}
                        placeholder="Type your official response..."
                        className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm outline-none focus:border-[#0B1C2D] transition-all"
                      />
                    </div>

                    <button
                      onClick={handleSend}
                      disabled={isSending}
                      className="w-11 h-11 bg-[#0B1C2D] text-white rounded-xl flex items-center justify-center shadow-lg shadow-[#0B1C2D]/20 disabled:opacity-50 transition-transform active:scale-95"
                    >
                      {isSending ? (
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      ) : (
                        <span className="material-symbols-outlined">send</span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TicketModal;
